// src/hooks/useTaskDetailModals.ts
// TaskDetail 組件的 Modal 狀態管理 Hook

import { useReducer, useCallback } from 'react';

// ============================================
// 類型定義
// ============================================

export interface ModalStates {
  split: boolean;
  deleteConfirm: boolean;
  dropConfirm: boolean;
  planSelector: boolean;
  beforeSelector: boolean;
  actionsMenu: boolean;
  // 欄位編輯 Picker（Priority/Energy/Location 改為 inline，不需要 modal）
  durationPicker: boolean;
  categoryPicker: boolean;
  objectivePicker: boolean;
}

type ModalName = keyof ModalStates;

type ModalAction =
  | { type: 'OPEN'; modal: ModalName }
  | { type: 'CLOSE'; modal: ModalName }
  | { type: 'TOGGLE'; modal: ModalName }
  | { type: 'CLOSE_ALL' };

export interface UseTaskDetailModalsReturn {
  modals: ModalStates;
  openModal: (name: ModalName) => void;
  closeModal: (name: ModalName) => void;
  toggleModal: (name: ModalName) => void;
  closeAllModals: () => void;
}

// ============================================
// 初始狀態
// ============================================

const initialState: ModalStates = {
  split: false,
  deleteConfirm: false,
  dropConfirm: false,
  planSelector: false,
  beforeSelector: false,
  actionsMenu: false,
  // 欄位編輯 Picker
  durationPicker: false,
  categoryPicker: false,
  objectivePicker: false,
};

// ============================================
// Reducer
// ============================================

function modalReducer(state: ModalStates, action: ModalAction): ModalStates {
  switch (action.type) {
    case 'OPEN':
      return { ...state, [action.modal]: true };
    case 'CLOSE':
      return { ...state, [action.modal]: false };
    case 'TOGGLE':
      return { ...state, [action.modal]: !state[action.modal] };
    case 'CLOSE_ALL':
      return initialState;
    default:
      return state;
  }
}

// ============================================
// Hook 實現
// ============================================

export function useTaskDetailModals(): UseTaskDetailModalsReturn {
  const [modals, dispatch] = useReducer(modalReducer, initialState);

  const openModal = useCallback((name: ModalName) => {
    dispatch({ type: 'OPEN', modal: name });
  }, []);

  const closeModal = useCallback((name: ModalName) => {
    dispatch({ type: 'CLOSE', modal: name });
  }, []);

  const toggleModal = useCallback((name: ModalName) => {
    dispatch({ type: 'TOGGLE', modal: name });
  }, []);

  const closeAllModals = useCallback(() => {
    dispatch({ type: 'CLOSE_ALL' });
  }, []);

  return {
    modals,
    openModal,
    closeModal,
    toggleModal,
    closeAllModals
  };
}
